import React, { useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import theme from './styles/theme';
import LoginPage from './components/auth/LoginPage';
import PlacesPage from './components/places/PlacesPage';
import SharedListPage from './components/lists/SharedListPage';
import { GOOGLE_MAPS_API_KEY } from './utils/firebase';
import { storePendingShare } from './utils/shareTarget';
import { recordCall } from './utils/apiUsage';
import { CircularProgress, Box } from '@mui/material';

const LIBRARIES = ['places'];

// Landing spot for the Web Share Target API (Android "Share to FoodList"
// from Instagram). Stash the shared link, then bounce to "/" — the normal
// auth-gated route takes it from there (including via /login if needed).
function ShareTargetRedirect() {
  const location = useLocation();
  // Synchronous (not an effect): must land in sessionStorage before the
  // <Navigate> child's effect fires and redirects away from this component.
  storePendingShare(location.search);
  return <Navigate to="/" replace />;
}

function AppInner() {
  const { user, loading } = useAuth();
  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  // One billable Maps JS load per PWA session (service-worker cache keeps
  // repeat opens off the network, but the Google client still counts them)
  useEffect(() => {
    if (mapsLoaded) recordCall('mapLoad');
  }, [mapsLoaded]);

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/list/:listId" element={<SharedListPage />} />
      <Route path="/share-target" element={<ShareTargetRedirect />} />
      <Route path="/*" element={
        user ? <PlacesPage mapsLoaded={mapsLoaded} /> : <Navigate to="/login" />
      } />
    </Routes>
  );
}

// Google Places Autocomplete renders its dropdown (.pac-container) in a portal
// with z-index ~1000 — below MUI's Dialog (1300), so it was invisible when
// typing inside the Add Place dialog. Also restyle it (and the Google Maps
// InfoWindow bubble, which is white by default) to match the dark theme.
const pacStyles = (
  <GlobalStyles styles={{
    '.pac-container': {
      zIndex: 2000,
      backgroundColor: '#16162a',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 10,
      boxShadow: '0 12px 32px rgba(0,0,0,0.6)',
      fontFamily: '"Manrope", "Helvetica", "Arial", sans-serif',
      marginTop: 4,
    },
    '.pac-item': {
      padding: '8px 12px',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      color: '#b0a89e',
      cursor: 'pointer',
      fontSize: '0.8rem',
    },
    '.pac-item:first-of-type': { borderTop: 'none' },
    '.pac-item:hover, .pac-item-selected, .pac-item-selected:hover': {
      backgroundColor: 'rgba(255,112,67,0.12)',
    },
    '.pac-item-query': { color: '#f2ede6', fontSize: '0.85rem' },
    '.pac-matched': { color: '#ff7043' },
    '.pac-icon': { filter: 'invert(0.7) hue-rotate(180deg)' },

    // Dark InfoWindow bubble
    '.gm-style .gm-style-iw-c': {
      backgroundColor: '#1e1e35 !important',
      borderRadius: '14px !important',
      border: '1px solid rgba(255,255,255,0.12)',
      boxShadow: '0 12px 32px rgba(0,0,0,0.55) !important',
      padding: '12px !important',
    },
    '.gm-style .gm-style-iw-d': {
      overflow: 'auto !important',
      backgroundColor: 'transparent',
    },
    '.gm-style .gm-style-iw-tc::after': {
      background: '#1e1e35 !important',
    },
    '.gm-style .gm-style-iw-c button.gm-ui-hover-effect': {
      filter: 'invert(1)',
    },
  }} />
);

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {pacStyles}
      <AuthProvider>
        <BrowserRouter>
          <AppInner />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
